import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { Settings, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ maintenance_mode: false, broadcast_banner: '' });

  const fetchSettings = useCallback(async () => {
    try {
      const r = await adminApi.getSettings();
      setSettings(r.data);
      setForm({ maintenance_mode: r.data.maintenance_mode || false, broadcast_banner: r.data.broadcast_banner || '' });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.updateSettings(form);
      setSettings(data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  if (loading) return <AdminLayout title="Settings"><div className="text-[#4E6974]">Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Platform Settings" breadcrumbs={[{ label: 'Settings' }]}>
      <div className="max-w-2xl space-y-6">
        {/* Maintenance mode */}
        <div className="bg-white rounded-xl border border-[#E8ECF0] p-5" data-testid="admin-settings-maintenance">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-[#0A2533]">Maintenance mode</h2>
              <p className="text-xs text-[#4E6974]">When enabled, all non-admin users will see a maintenance page.</p>
            </div>
            <button onClick={() => setForm(f => ({ ...f, maintenance_mode: !f.maintenance_mode }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.maintenance_mode ? 'bg-red-500' : 'bg-[#E0E0E0]'}`}
              data-testid="admin-settings-maintenance-toggle">
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.maintenance_mode ? 'translate-x-5' : ''}`} />
            </button>
          </div>
          {form.maintenance_mode && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-xs rounded-lg p-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Maintenance mode is active. Users cannot access the platform.
            </div>
          )}
        </div>

        {/* Broadcast banner */}
        <div className="bg-white rounded-xl border border-[#E8ECF0] p-5" data-testid="admin-settings-banner">
          <h2 className="text-sm font-semibold text-[#0A2533] mb-1">Broadcast banner</h2>
          <p className="text-xs text-[#4E6974] mb-3">Text shown to all users at the top of the platform. Leave blank to hide.</p>
          <textarea data-testid="admin-settings-banner-input" value={form.broadcast_banner} onChange={e => setForm(f => ({ ...f, broadcast_banner: e.target.value }))}
            className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm min-h-[80px] focus:border-[#0097A7] outline-none"
            placeholder="e.g. Scheduled maintenance on Saturday 10pm–2am AEST..." />
        </div>

        {/* Default tier limits */}
        {settings?.default_tier_limits && (
          <div className="bg-white rounded-xl border border-[#E8ECF0] p-5" data-testid="admin-settings-tier-limits">
            <h2 className="text-sm font-semibold text-[#0A2533] mb-3">Default tier limits (max users)</h2>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(settings.default_tier_limits).map(([tier, limit]) => (
                <div key={tier} className="flex items-center justify-between bg-[#F8FAFB] rounded-lg p-3">
                  <span className="text-sm text-[#4E6974] capitalize">{tier}</span>
                  <span className="text-sm font-medium text-[#0A2533]">{limit} users</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save button */}
        <button data-testid="admin-settings-save-btn" onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white text-sm font-medium rounded-lg transition-colors">
          <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>
    </AdminLayout>
  );
}
